import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { dataExportService } from "@/services/data-export-service";
import {
  ExportJob,
  ExportJobsQuery,
  ExportTargetType,
  StartExportDto,
  isTerminalExportStatus,
} from "@/types/data-export";

const EXPORT_JOB_POLL_INTERVAL_MS = 2000;
const ACTIVE_TARGET_SCAN_LIMIT = 50;

export const dataExportKeys = {
  all: ["data-export"] as const,
  jobs: () => [...dataExportKeys.all, "jobs"] as const,
  jobList: (query?: ExportJobsQuery) =>
    [...dataExportKeys.jobs(), "list", query ?? {}] as const,
  job: (jobId: string) => [...dataExportKeys.jobs(), "detail", jobId] as const,
  activeForTarget: (targetType: ExportTargetType, targetId: string) =>
    [...dataExportKeys.jobs(), "active", targetType, targetId] as const,
};

/**
 * Polls a single export job every 2s while non-terminal and stops once it
 * reaches `COMPLETED | FAILED`. Each poll re-fetches `GET /jobs/:id`, which
 * mints a fresh (ephemeral) `downloadUrl` — never cache the URL.
 */
export const useExportJob = (jobId: string | null) =>
  useQuery({
    queryKey: dataExportKeys.job(jobId ?? "none"),
    queryFn: () => dataExportService.getJob(jobId as string),
    enabled: Boolean(jobId),
    // Always refetch: the signed downloadUrl is short-lived and minted per request.
    staleTime: 0,
    refetchInterval: (query) =>
      isTerminalExportStatus(query.state.data?.status)
        ? false
        : EXPORT_JOB_POLL_INTERVAL_MS,
  });

export const useExportJobs = (query?: ExportJobsQuery) =>
  useQuery({
    queryKey: dataExportKeys.jobList(query),
    queryFn: () => dataExportService.getJobs(query),
    // Intentional deviation from the hooks.md 5-min default: the audit log is
    // volatile while exports run; keepPreviousData avoids an empty flash on
    // paging/filtering.
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });

/**
 * Resolves the in-flight (non-terminal) export job for a target, if any, so a
 * screen can disable its export button and link to the running job. Scans the
 * most recent {@link ACTIVE_TARGET_SCAN_LIMIT} jobs client-side (the list has no
 * `targetId` filter). The backend still enforces single-in-flight via 409, so
 * this is a UX hint, not a guard. SUPER_ADMIN-only (uses the jobs list).
 */
export const useActiveExportJobForTarget = (
  targetType: ExportTargetType,
  targetId: string | null | undefined,
  enabled = true,
) =>
  useQuery({
    queryKey: dataExportKeys.activeForTarget(targetType, targetId ?? "none"),
    queryFn: async (): Promise<ExportJob | null> => {
      const response = await dataExportService.getJobs({
        targetType,
        limit: ACTIVE_TARGET_SCAN_LIMIT,
      });
      return (
        response.data.find(
          (job) =>
            job.targetId === targetId && !isTerminalExportStatus(job.status),
        ) ?? null
      );
    },
    enabled: enabled && Boolean(targetId),
    staleTime: 15 * 1000,
  });

type StartExportVariables = { id: string; dto: StartExportDto };
type ExportStarter = (id: string, dto: StartExportDto) => Promise<ExportJob>;

const useStartExportMutation = (starter: ExportStarter) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: StartExportVariables) => starter(id, dto),
    onSuccess: (job, variables) => {
      // Seed the job cache so the status panel can poll immediately.
      queryClient.setQueryData(dataExportKeys.job(job.id), job);

      // A guest export without `tenantId` is a HOTEL_ADMIN call. Hotel admins can
      // read a single job (GET /jobs/:jobId) but NOT the jobs list / active-job
      // queries (both SUPER_ADMIN-only), so skip the broad `jobs()` invalidation
      // for them — the seeded detail above is polled directly by the status view.
      const isHotelScopedGuestCall =
        job.targetType === ExportTargetType.GUEST && !variables.dto.tenantId;
      if (!isHotelScopedGuestCall) {
        queryClient.invalidateQueries({ queryKey: dataExportKeys.jobs() });
      }
    },
  });
};

export const useStartGuestExport = () =>
  useStartExportMutation((id, dto) =>
    dataExportService.startGuestExport(id, dto),
  );

export const useStartUserExport = () =>
  useStartExportMutation((id, dto) =>
    dataExportService.startUserExport(id, dto),
  );
